/**
 * User bounty API client wrapping the backend /users/:userId/bounties*
 * endpoints. Handles create/update/delete of a user's own bounties, paginated
 * fetches (optionally filtered by status via PrivateBountyQueryParams),
 * pursuit listings, and multipart image upload/edit for bounty photos.
 * Consumed by BountyCreateEdit, SubheadedBountyDisplay, and ItemCollection.
 */
import { api } from "@/lib/axios";
import { Bounty } from "@/types/bounty";
import { Category, Sex } from "@/types/category";
import { BountyStatus } from "@/types/bounty";
import { PaginatedResponse } from "@/types/pagination";
import { fetchPaginatedItems, PaginationQueryParams } from "./paginated";

export interface PrivateBountyQueryParams extends PaginationQueryParams {
    category?: Category | null;
    sex?: Sex | null;
    categoryType?: string | null;
    status?: BountyStatus | null;
}

export const UserBountyAPI = {
    createBounty: async (userId: number, bounty: Bounty) => {
        const { data } = await api.post<{ bounty: Bounty }>(`/users/${userId}/bounties`, {
            bounty,
        });
        return data;
    },
    deleteBounty: async (userId: number, bountyId: number) => {
        const { data } = await api.delete(`/users/${userId}/bounties/${bountyId}`);
        return data;
    },
    fetchBounties: async (
        userId: number,
        params: PrivateBountyQueryParams = {}
    ): Promise<PaginatedResponse<Bounty>> => {
        return fetchPaginatedItems<Bounty>(`/users/${userId}/bounties`, params);
    },
    fetchBountiesByStatus: async (
        userId: number,
        status: BountyStatus,
        params: PrivateBountyQueryParams = {}
    ): Promise<PaginatedResponse<Bounty>> => {
        return fetchPaginatedItems<Bounty>(`/users/${userId}/bounties/status/${status}`, params);
    },
    fetchPursuits: async (
        userId: number,
        params: PrivateBountyQueryParams = {}
    ): Promise<PaginatedResponse<Bounty>> => {
        return fetchPaginatedItems<Bounty>(`/users/${userId}/pursuits`, params);
    },
    uploadBountyImages: async (userId: number, bountyId: number, formData: FormData) => {
        const { data } = await api.post(
            `/users/${userId}/bounties/${bountyId}/upload-images`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
        return data;
    },
    updateBounty: async (userId: number, bounty: Bounty) => {
        const { data } = await api.put(`/users/${userId}/bounties/${bounty.id!}`, {
            bounty,
        });
        return data;
    },
    editBountyImages: async (userId: number, bountyId: number, formData: FormData) => {
        const { data } = await api.post(
            `/users/${userId}/bounties/${bountyId}/edit-images`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
        return data;
    },
};
